/**
 * Dates, times, numbers and money for the whole CRM, in one place.
 *
 * Rules (owner, 2026-10-17):
 *  - Uzbek (Latin) first. The language is the user's stored `crm-lang`, else Uzbek; pass
 *    `{ lang }` (or a bare 'uz' | 'ru' | 'en') to answer in another one.
 *  - Time is always 24-hour ("14:30"), never AM/PM. Intl is only used to move an instant into
 *    a named time zone, and it is always pinned to hourCycle 'h23'.
 *  - Nothing else in src/ may call toLocale*String or Intl.DateTimeFormat. Chromium has no
 *    `uz` CLDR data ("M10 17, Sat"), so month and weekday names are hand-rolled tables for all
 *    three languages, which also keeps the output identical on every browser.
 *  - Numbers use a dot decimal, at most one decimal, and NBSP thousand groups: "1 497 000 so'm".
 *  - ISO strings ("2026-09-05") never reach the screen. Exports (CSV) keep ISO dates and raw
 *    numbers and must not use these helpers.
 *
 * Every date helper takes (value, opts?). `value` is a Date, an epoch in ms, an ISO timestamp,
 * or a bare "YYYY-MM-DD" (a calendar day: never shifted by a time zone). A missing or invalid
 * value gives an em dash. `opts`: { lang, timeZone, year, now }. `timeZone` is an IANA name
 * ('Asia/Tashkent', 'UTC'); omitted means the browser's own zone.
 */
import { asLang, getActiveLang } from '../i18n/lang';

export const NBSP = ' ';
export const EM_DASH = '—';
const EN_DASH = '–';
const DEFAULT_CURRENCY = 'UZS';

/* ── Vocabulary (hand-rolled: Chromium ships no `uz` CLDR data) ────────────────────────────── */

// Month used with a day: Russian takes the genitive ("17 октября").
const MONTHS = {
  uz: ['yanvar', 'fevral', 'mart', 'aprel', 'may', 'iyun', 'iyul', 'avgust', 'sentabr', 'oktabr', 'noyabr', 'dekabr'],
  ru: ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'],
  en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
};
// Month standing alone (a calendar header): Russian takes the nominative.
const MONTHS_ALONE = {
  uz: MONTHS.uz,
  ru: ['январь', 'февраль', 'март', 'апрель', 'май', 'июнь', 'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь'],
  en: MONTHS.en,
};
const MONTHS_SHORT = {
  uz: ['yan', 'fev', 'mar', 'apr', 'may', 'iyun', 'iyul', 'avg', 'sen', 'okt', 'noy', 'dek'],
  ru: ['янв.', 'февр.', 'мар.', 'апр.', 'мая', 'июн.', 'июл.', 'авг.', 'сент.', 'окт.', 'нояб.', 'дек.'],
  en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
};
// Sunday first, the order of Date#getDay().
const WEEKDAYS = {
  uz: ['Yakshanba', 'Dushanba', 'Seshanba', 'Chorshanba', 'Payshanba', 'Juma', 'Shanba'],
  ru: ['Воскресенье', 'Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота'],
  en: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
};
const WEEKDAYS_SHORT = {
  uz: ['Ya', 'Du', 'Se', 'Ch', 'Pa', 'Ju', 'Sh'],
  ru: ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'],
  en: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
};
const MONDAY_FIRST = [1, 2, 3, 4, 5, 6, 0];

const RELATIVE = {
  uz: { now: 'hozir', minutes: (n) => `${n} daqiqa oldin`, hours: (n) => `${n} soat oldin`, yesterday: 'kecha' },
  ru: { now: 'сейчас', minutes: (n) => `${n} мин. назад`, hours: (n) => `${n} ч. назад`, yesterday: 'вчера' },
  en: { now: 'just now', minutes: (n) => `${n} min ago`, hours: (n) => `${n} h ago`, yesterday: 'yesterday' },
};
const DURATION_UNITS = {
  uz: { h: 'soat', m: 'daqiqa', s: 'soniya' },
  ru: { h: 'ч', m: 'мин', s: 'с' },
  en: { h: 'h', m: 'min', s: 's' },
};
const FREE = { uz: 'Bepul', ru: 'Бесплатно', en: 'Free' };
const SUM = { uz: "so'm", ru: 'сум', en: 'UZS' };
// Compact suffixes for thousand, million, billion. Words get a leading NBSP, letters do not.
const COMPACT = {
  uz: [`${NBSP}ming`, `${NBSP}mln`, `${NBSP}mlrd`],
  ru: [`${NBSP}тыс.`, `${NBSP}млн`, `${NBSP}млрд`],
  en: ['k', 'M', 'B'],
};

/* ── Options and language ──────────────────────────────────────────────────────────────────── */

// Second argument: a bare language ('uz', or a locale tag such as 'ru-RU') or an options object.
// Anything else (for example the tick index that Recharts hands to a tickFormatter) is ignored.
function opt(arg) {
  if (typeof arg === 'string') return { lang: arg };
  return arg && typeof arg === 'object' && !(arg instanceof Date) ? arg : {};
}

function langOf(o) {
  const tag = typeof o.lang === 'string' ? o.lang.slice(0, 2).toLowerCase() : null;
  return asLang(tag) || getActiveLang();
}

/** The language the helpers answer in right now: the user's stored choice, else Uzbek. */
export function getLang(lang) {
  return langOf(opt(lang));
}

const pad2 = (n) => String(n).padStart(2, '0');

/* ── Value → calendar parts ────────────────────────────────────────────────────────────────── */

const DAY_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
const CLOCK_ONLY = /^(\d{1,2}):(\d{2})(?::\d{2})?$/;

function dayParts(y, m, d) {
  const probe = new Date(Date.UTC(y, m, d));
  // Date.UTC rolls 31 February over to March: refuse a day that does not exist.
  if (probe.getUTCFullYear() !== y || probe.getUTCMonth() !== m || probe.getUTCDate() !== d) return null;
  return { y, m, d, h: 0, min: 0, s: 0, wd: probe.getUTCDay(), dayOnly: true };
}

const zoneFormatters = new Map();
function zoneFormatter(timeZone) {
  if (!zoneFormatters.has(timeZone)) {
    let format = null;
    try {
      format = new Intl.DateTimeFormat('en-US', {
        timeZone,
        hourCycle: 'h23',
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
      });
    } catch {
      format = null; // not a time zone this browser knows: use the browser's own
    }
    zoneFormatters.set(timeZone, format);
  }
  return zoneFormatters.get(timeZone);
}

function instantParts(date, timeZone) {
  if (timeZone === 'UTC') {
    return {
      y: date.getUTCFullYear(), m: date.getUTCMonth(), d: date.getUTCDate(),
      h: date.getUTCHours(), min: date.getUTCMinutes(), s: date.getUTCSeconds(), wd: date.getUTCDay(),
    };
  }
  const format = timeZone ? zoneFormatter(timeZone) : null;
  if (!format) {
    return {
      y: date.getFullYear(), m: date.getMonth(), d: date.getDate(),
      h: date.getHours(), min: date.getMinutes(), s: date.getSeconds(), wd: date.getDay(),
    };
  }
  const got = {};
  format.formatToParts(date).forEach((part) => {
    if (part.type !== 'literal') got[part.type] = parseInt(part.value, 10);
  });
  return {
    y: got.year, m: got.month - 1, d: got.day, h: got.hour % 24, min: got.minute, s: got.second,
    wd: new Date(Date.UTC(got.year, got.month - 1, got.day)).getUTCDay(),
  };
}

function toDate(value) {
  if (value === null || value === undefined || value === '') return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Calendar parts of a value in the requested zone, or null when it is not a date. */
function partsOf(value, timeZone) {
  if (typeof value === 'string') {
    const text = value.trim();
    const day = DAY_ONLY.exec(text);
    if (day) return dayParts(Number(day[1]), Number(day[2]) - 1, Number(day[3]));
    const clock = CLOCK_ONLY.exec(text);
    if (clock) {
      const h = Number(clock[1]);
      const min = Number(clock[2]);
      return h < 24 && min < 60 ? { y: 0, m: 0, d: 1, h, min, s: 0, wd: 0, clockOnly: true } : null;
    }
    const date = toDate(text);
    return date ? instantParts(date, timeZone) : null;
  }
  const date = toDate(value);
  return date ? instantParts(date, timeZone) : null;
}

function nowParts(o) {
  const date = o.now === undefined || o.now === null ? new Date() : (toDate(o.now) || new Date());
  return instantParts(date, o.timeZone);
}

// 'always' | 'never' | 'auto' (only when it is not this year)
function wantsYear(mode, parts, o) {
  if (mode === true || mode === 'always') return true;
  if (mode === false || mode === 'never') return false;
  return parts.y !== nowParts(o).y;
}

/* ── Names ─────────────────────────────────────────────────────────────────────────────────── */

const upperFirst = (text) => text.charAt(0).toUpperCase() + text.slice(1);

/** Month name for a calendar header: "Oktabr" · "Октябрь" · "October". monthIndex is 0–11. */
export function formatMonthName(monthIndex, lang) {
  if (!Number.isFinite(Number(monthIndex))) return '';
  const code = langOf(opt(lang));
  return upperFirst(MONTHS_ALONE[code][((Math.trunc(Number(monthIndex)) % 12) + 12) % 12]);
}

/** "Oktabr 2026" · "Октябрь 2026" · "October 2026" — a calendar header. monthIndex is 0–11. */
export function formatMonthYear(year, monthIndex, lang) {
  return `${formatMonthName(monthIndex, lang)} ${year}`;
}

/** Two-letter weekday heads, Monday first: Du Se Ch Pa Ju Sh Ya · Пн Вт Ср Чт Пт Сб Вс · Mo Tu We Th Fr Sa Su. */
export function weekdayShortLabels(lang) {
  const names = WEEKDAYS_SHORT[langOf(opt(lang))];
  return MONDAY_FIRST.map((i) => names[i]);
}

/** Full weekday names, Monday first. */
export function weekdayLabels(lang) {
  const names = WEEKDAYS[langOf(opt(lang))];
  return MONDAY_FIRST.map((i) => names[i]);
}

/** Full month names (with a day: Russian genitive), January first. */
export function monthLabels(lang) {
  return MONTHS[langOf(opt(lang))].map(upperFirst);
}

/* ── Dates and times ───────────────────────────────────────────────────────────────────────── */

// "17-oktabr" in Uzbek (the day takes a hyphen), "17 октября" / "17 Oct" elsewhere.
// medium: the month as the language usually writes it beside a day (English abbreviates);
// short: always abbreviated (axes); long: always in full ("17 October", with a weekday).
function dayMonth(parts, code, style = 'medium') {
  const names = style === 'short' || (style === 'medium' && code === 'en') ? MONTHS_SHORT : MONTHS;
  const month = names[code][parts.m];
  return code === 'uz' ? `${parts.d}-${month}` : `${parts.d} ${month}`;
}

const clockOf = (parts) => `${pad2(parts.h)}:${pad2(parts.min)}`;

/**
 * "17-oktabr 2026" · "17 октября 2026" · "17 Oct 2026". Detail views, and anything the user reads or prints.
 * `year`: 'always' (default) | 'auto' (only when not this year) | 'never'.
 */
export function formatDate(value, options) {
  const o = opt(options);
  const parts = partsOf(value, o.timeZone);
  if (!parts || parts.clockOnly) return EM_DASH;
  const code = langOf(o);
  const label = dayMonth(parts, code);
  return wantsYear(o.year === undefined ? 'always' : o.year, parts, o) ? `${label} ${parts.y}` : label;
}

/**
 * "17-oktabr, 14:30" · "17 октября, 14:30" · "17 Oct, 14:30"; the year joins when it is not this
 * year ("17-oktabr 2026, 14:30"). Lists, activity. A bare day has no time, so it reads as a date.
 */
export function formatDateTime(value, options) {
  const o = opt(options);
  const parts = partsOf(value, o.timeZone);
  if (!parts || parts.clockOnly) return EM_DASH;
  const code = langOf(o);
  const label = dayMonth(parts, code);
  const dated = wantsYear(o.year === undefined ? 'auto' : o.year, parts, o) ? `${label} ${parts.y}` : label;
  return parts.dayOnly ? dated : `${dated}, ${clockOf(parts)}`;
}

/** "17-okt" · "17 окт." · "17 Oct" — chart axes, dense cells. `year`: 'never' (default) | 'always' | 'auto'. */
export function formatShortDate(value, options) {
  const o = opt(options);
  const parts = partsOf(value, o.timeZone);
  if (!parts || parts.clockOnly) return EM_DASH;
  const label = dayMonth(parts, langOf(o), 'short');
  return wantsYear(o.year === undefined ? 'never' : o.year, parts, o) ? `${label} ${parts.y}` : label;
}

/** "17-okt, 14:30" · "17 окт., 14:30" · "17 Oct, 14:30" — dense cells with a time. */
export function formatShortDateTime(value, options) {
  const o = opt(options);
  const parts = partsOf(value, o.timeZone);
  if (!parts || parts.clockOnly) return EM_DASH;
  const label = dayMonth(parts, langOf(o), 'short');
  const dated = wantsYear(o.year === undefined ? 'never' : o.year, parts, o) ? `${label} ${parts.y}` : label;
  return parts.dayOnly ? dated : `${dated}, ${clockOf(parts)}`;
}

/** "Shanba, 17-oktabr" · "Суббота, 17 октября" · "Saturday, 17 October" — the top bar, a greeting. */
export function formatLongDate(value, options) {
  const o = opt(options);
  const parts = partsOf(value, o.timeZone);
  if (!parts || parts.clockOnly) return EM_DASH;
  const code = langOf(o);
  const label = `${WEEKDAYS[code][parts.wd]}, ${dayMonth(parts, code, 'long')}`;
  return wantsYear(o.year === undefined ? 'never' : o.year, parts, o) ? `${label} ${parts.y}` : label;
}

/** "05 Oktabr 2026" — the text of a date picker field: a two-digit day, the month name, the year. */
export function formatPickerDate(value, options) {
  const o = opt(options);
  const parts = partsOf(value, o.timeZone);
  if (!parts || parts.clockOnly) return '';
  return `${pad2(parts.d)} ${formatMonthName(parts.m, langOf(o))} ${parts.y}`;
}

/**
 * "1-iyun – 31-avgust 2025": two dates as one range. Inside one year the start drops its year.
 * With only one end it is that date; with neither, an em dash.
 */
export function formatDateRange(from, to, options) {
  const o = opt(options);
  const a = partsOf(from, o.timeZone);
  const b = partsOf(to, o.timeZone);
  if (!a && !b) return EM_DASH;
  if (!a) return formatDate(to, o);
  if (!b) return formatDate(from, o);
  const code = langOf(o);
  const start = a.y === b.y ? dayMonth(a, code) : formatDate(from, o);
  return `${start} ${EN_DASH} ${formatDate(to, o)}`;
}

/** "14:30" — always 24-hour. A bare "9:05" or "09:05:30" is normalised to "09:05". */
export function formatTime(value, options) {
  const o = opt(options);
  const parts = partsOf(value, o.timeZone);
  if (!parts || parts.dayOnly) return EM_DASH;
  return clockOf(parts);
}

/**
 * How long ago, then a date: "hozir" · "5 daqiqa oldin" · "2 soat oldin" · "kecha, 14:30", and from
 * the day before yesterday on formatDateTime(). Feeds and last seen. `now` (ms, Date or ISO) is the
 * reference time when the server's clock should win over the browser's.
 */
export function formatRelative(value, options) {
  const o = opt(options);
  const parts = partsOf(value, o.timeZone);
  if (!parts || parts.clockOnly) return EM_DASH;
  if (parts.dayOnly) return formatDate(value, { ...o, year: 'auto' });
  const date = toDate(value);
  const nowDate = o.now === undefined || o.now === null ? new Date() : (toDate(o.now) || new Date());
  const code = langOf(o);
  const words = RELATIVE[code];
  const elapsed = nowDate.getTime() - date.getTime();

  if (elapsed < 0) return -elapsed < 60000 ? words.now : formatDateTime(value, { ...o, year: 'auto' });
  const minutes = Math.floor(elapsed / 60000);
  if (minutes < 1) return words.now;
  if (minutes < 60) return words.minutes(minutes);
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return words.hours(hours);

  const today = instantParts(nowDate, o.timeZone);
  const days = Math.round((Date.UTC(today.y, today.m, today.d) - Date.UTC(parts.y, parts.m, parts.d)) / 86400000);
  if (days === 1) return `${words.yesterday}, ${clockOf(parts)}`;
  return formatDateTime(value, { ...o, year: 'auto' });
}

/**
 * A length of time from seconds. Default is a clock, "3:20" or "1:05:00". `style: 'long'` is
 * words: "2 soat 15 daqiqa" · "2 ч 15 мин" · "2 h 15 min" (just seconds under a minute).
 */
export function formatDuration(seconds, options) {
  const o = opt(options);
  const total = Math.max(0, Math.round(Number(seconds) || 0));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (o.style === 'long') {
    const unit = DURATION_UNITS[langOf(o)];
    if (total < 60) return `${s} ${unit.s}`;
    return [h ? `${h} ${unit.h}` : '', m ? `${m} ${unit.m}` : ''].filter(Boolean).join(' ');
  }
  return h > 0 ? `${h}:${pad2(m)}:${pad2(s)}` : `${m}:${pad2(s)}`;
}

/* ── Calendar-day strings ("YYYY-MM-DD"), for the date pickers and filters ─────────────────── */

/** The calendar day of a value as "YYYY-MM-DD" in the requested zone; null when it is not a date. */
export function toYmd(value, options) {
  const o = opt(options);
  const parts = partsOf(value, o.timeZone);
  if (!parts || parts.clockOnly) return null;
  return `${String(parts.y).padStart(4, '0')}-${pad2(parts.m + 1)}-${pad2(parts.d)}`;
}

/** Today as "YYYY-MM-DD" (Tashkent time with `timeZone: 'Asia/Tashkent'`). */
export function todayYmd(options) {
  return toYmd(new Date(), options);
}

/** "YYYY-MM-DD" as a Date at local noon (no daylight-saving flip can move the day); null if invalid. */
export function ymdToDate(ymd) {
  const match = DAY_ONLY.exec(String(ymd || '').trim());
  if (!match || !dayParts(Number(match[1]), Number(match[2]) - 1, Number(match[3]))) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12, 0, 0, 0);
}

/* ── Numbers and money ─────────────────────────────────────────────────────────────────────── */

function group(digits) {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);
}

/**
 * "1 497 000" · "12.5": a dot decimal, at most `decimals` (default 1) places, NBSP thousand groups.
 * Not a number counts as 0.
 */
export function formatNumber(value, options) {
  const o = opt(options);
  const decimals = Number.isInteger(o.decimals) ? o.decimals : 1;
  const n = Number(value);
  const safe = Number.isFinite(n) ? n : 0;
  const [whole, fraction = ''] = Math.abs(safe).toFixed(decimals).split('.');
  const trimmed = fraction.replace(/0+$/, '');
  const body = `${group(whole)}${trimmed ? `.${trimmed}` : ''}`;
  return safe < 0 && /[1-9]/.test(`${whole}${trimmed}`) ? `-${body}` : body;
}

/** "71.4%" — `value` is already a percentage. */
export function formatPercent(value, options) {
  return `${formatNumber(value, options)}%`;
}

/**
 * 2 593 000 → "2.6 mln" · "2.6 млн" · "2.6M"; 398 000 → "398 ming"; 1 200 000 000 → "1.2 mlrd".
 * No currency: put the unit in the card subtitle. One decimal below 10, none above.
 */
export function formatCompact(value, options) {
  const o = opt(options);
  const suffixes = COMPACT[langOf(o)];
  const n = Number(value);
  const safe = Number.isFinite(n) ? n : 0;
  const sign = safe < 0 ? '-' : '';
  const abs = Math.abs(safe);
  if (abs < 1000) return `${sign}${formatNumber(abs, { decimals: 1 })}`;

  let tier = abs >= 1e9 ? 3 : abs >= 1e6 ? 2 : 1;
  let scaled = abs / 1000 ** tier;
  const round = (x) => (x < 10 ? Math.round(x * 10) / 10 : Math.round(x));
  let shown = round(scaled);
  if (shown >= 1000 && tier < 3) { // 999 600 is "1 mln", not "1000 ming"
    tier += 1;
    scaled = abs / 1000 ** tier;
    shown = round(scaled);
  }
  return `${sign}${shown}${suffixes[tier - 1]}`;
}

const SUM_CODES = new Set([DEFAULT_CURRENCY, 'SUM', "SO'M", 'СУМ']);
const isSum = (currency) => SUM_CODES.has(String(currency || DEFAULT_CURRENCY).trim().toUpperCase());

/** The word for a currency code: UZS → "so'm" · "сум" · "UZS"; any other code stays as written. */
export function currencyLabel(currency, options) {
  if (isSum(currency)) return SUM[langOf(opt(options))];
  return String(currency).trim().toUpperCase();
}

// formatMoney(amount, 'UZS', opts) · formatMoney(amount, opts)
function moneyArgs(currency, options) {
  if (currency && typeof currency === 'object') return { currency: DEFAULT_CURRENCY, o: currency };
  return { currency: typeof currency === 'string' && currency ? currency : DEFAULT_CURRENCY, o: opt(options) };
}

/**
 * "1 497 000 so'm" · "1 497 000 сум" · "1 497 000 UZS". Zero is "Bepul" · "Бесплатно" · "Free"
 * (pass `{ free: false }` for a total that should read "0 so'm").
 */
export function formatMoney(amount, currency, options) {
  const { currency: code, o } = moneyArgs(currency, options);
  const n = Number(amount) || 0;
  if (!n && o.free !== false) return FREE[langOf(o)];
  // So'm has no minor unit in use; any other currency keeps its cents.
  return `${formatNumber(n, { decimals: isSum(code) ? 0 : 2 })}${NBSP}${currencyLabel(code, o)}`;
}

/** "2.6 mln so'm" · "398 ming so'm" · "2.6M UZS". KPI tiles and dashboards. */
export function formatMoneyCompact(amount, currency, options) {
  const { currency: code, o } = moneyArgs(currency, options);
  return `${formatCompact(amount, o)}${NBSP}${currencyLabel(code, o)}`;
}

/** "+998 90 123 45 67" from 998901234567, +998901234567 or 901234567; anything else is returned as typed. */
export function formatPhone(value) {
  const text = String(value ?? '').trim();
  if (!text) return '';
  const digits = text.replace(/\D/g, '');
  const local = digits.length === 12 && digits.startsWith('998') ? digits.slice(3) : (digits.length === 9 ? digits : null);
  if (!local) return text;
  return `+998 ${local.slice(0, 2)} ${local.slice(2, 5)} ${local.slice(5, 7)} ${local.slice(7, 9)}`;
}
